import {
  compact,
  find,
  get,
  includes,
  isArray,
  isString,
  isUndefined,
  join,
  map,
  parseInt,
  reduce,
  size,
  split,
  tail,
} from "lodash";
import { getAffectColor } from "@linkfluence/models/affect";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { parse as parseEmoji } from "@tootools/emoji";
import { reverseUrl } from "@tootools/string";
import { tr } from "@radarly/i18n";

export interface Value {
  id: number | string;
  label: string;
  color?: string;
  picture?: string;
  category?: string;
  min?: number;
  max?: number;
}

export interface FullFacet {
  id: string;
  type: string;
  mode: string;
  icon: string;
  label: string;
  values: Value[];
  description?: string;
  custom?: boolean;
  facetType?: string;
  canBeExcluded?: boolean;
  metas?: { min: number; max: number };
  category?: string;
}

interface FormatedFacetValue {
  id: number | string;
  label: string;
  color?: string;
}

export interface FormatedFacet {
  id: string;
  type: string;
  mode: string;
  icon: string;
  label: string;
  values: FormatedFacetValue[];
  custom?: boolean;
  facetType?: string;
  canBeExcluded?: boolean;
}

export enum Categories {
  basic = "FACETS::FILTERS::BASIC",
  team = "FACETS::FILTERS::TEAM",
  text = "FACETS::FILTERS::TEXT",
  image = "FACETS::FILTERS::IMAGE",
  posts = "FACETS::FILTERS::POSTS",
  author = "FACETS::FILTERS::AUTHOR",
  reviews = "FACETS::FILTERS::REVIEWS",
  misc = "FACETS::FILTERS::MISC",
}

export type FilterCategories = Record<keyof typeof Categories, FullFacet[]>;

type FacetValueLabel = Value & {
  inclusion: boolean;
  isHtml?: boolean;
};

export interface FacetLabels {
  id: string;
  labels: FacetValueLabel[];
  icon?: string;
  color?: string;
}

export function isFacet(facet: FullFacet, facetName: string) {
  return facet && facet.id === facetName;
}

export function getFacetValueLabel(facet: string, label: string | number) {
  switch (facet) {
    case Facet.Hashtags:
      return "#" + label;
    case Facet.Mentions:
      return "@" + label;
    case Facet.Entities:
      return ("" + (label || "")).split(".").join(" ");
    case Facet.EmojiCharts:
      return parseEmoji("" + label);
    case Facet.SharedUrl:
      return reverseUrl("" + label);
    case Facet.Affects:
    case Facet.AffectsNegative:
    case Facet.AffectsPositive:
      return join(tail(split("" + label, ".")), " ");
    default:
      return "" + label;
  }
}

export function getLabelsFromFacet(
  facet: FullFacet,
  values: Array<string | number>,
  inclusion: boolean = true
): FacetLabels {
  function getColorFromValue(value: string | number) {
    if (isFacet(facet, Facet.Affects)) {
      return getAffectColor("" + value);
    } else {
      return null;
    }
  }

  function getFacetValue(value: string | number) {
    let facetValue: Value = undefined;
    if (facet && isArray(facet.values) && facet.values.length > 0) {
      facetValue = find(facet.values, { id: value });
      if (isUndefined(facetValue)) {
        facetValue = find(facet.values, { id: ("" + value).toLowerCase() });
      }
      if (isUndefined(facetValue)) {
        // retry with a number
        facetValue = find(facet.values, {
          id: isString(value) ? parseInt(value) : value,
        });
      }
    }
    return facetValue;
  }

  function getColorLabels(labels: FacetValueLabel[]) {
    const colors = compact(map(labels, "color"));
    if (size(colors) === 1) {
      return colors[0];
    } else {
      return "";
    }
  }

  function getLabelsFromFilterValues() {
    return reduce(
      values,
      (acc: FacetValueLabel[], value) => {
        const facetValue = getFacetValue(value);
        if (!isUndefined(facetValue)) {
          acc.push({
            ...facetValue,
            inclusion,
          });
        } else {
          acc.push({
            id: value,
            color: getColorFromValue(value),
            label: "" + value,
            isHtml: isFacet(facet, Facet.EmojiCharts),
            inclusion,
          });
        }
        return acc;
      },
      []
    );
  }

  const labels = getLabelsFromFilterValues();
  if (facet && size(labels)) {
    return {
      id: facet.id,
      labels: labels,
      icon: facet.icon,
      color: getColorLabels(labels),
    };
  } else {
    return null;
  }
}

export function getPanelFromFacet(
  facet: FullFacet,
  isGlobalInsightPage: boolean = false
) {
  if (facet.custom) {
    return size(facet.values) > 10 ? "big-list" : "checkboxes";
  } else if (facet.id === Facet.ContentClassification) {
    return "classification";
  } else {
    if (facet.id === Facet.Focuses && isGlobalInsightPage) {
      return "queries-big-list";
    }
    if (
      includes(
        [
          Facet.Focuses, // TODO REMOVE ONCE PROJECT LEVEL IP ENABLED
          Facet.InfluencersTags,
          Facet.Corpus,
          Facet.ProfileType,
          Facet.AccountType,
          Facet.SpamType,
          Facet.Languages,
          Facet.Media,
          Facet.GeoLocations,
          Facet.GeoTags,
          Facet.Towns,
        ],
        facet.id
      )
    ) {
      return size(facet.values) > 30 ? "big-list" : "checkboxes";
    } else if (
      includes([Facet.Product, Facet.ProductRange, Facet.Franchise], facet.id)
    ) {
      return "big-list";
    } else if (
      includes(
        [
          Facet.Platforms,
          Facet.Tones,
          Facet.ImageRecognitionAge,
          Facet.ImageRecognitionEmotions,
          Facet.ImageRecognitionGenders,
          Facet.Devices,
          Facet.DevicesPublishingMethods,
          Facet.DeviceOperatingSystems,
          Facet.DevicesPublishingMethods,
          Facet.ImageRecognitionPersons,
          Facet.Emotions,
          Facet.ImageRecognitionPersonIdentifications,
          Facet.ImageRecognitionLogos,
          Facet.Genders,
        ],
        facet.id
      )
    ) {
      return "chips";
    } else if (includes([Facet.MaritalStatus], facet.id)) {
      return "sandboxed-chips";
    } else if (
      includes([Facet.Mentions, Facet.Hashtags, Facet.Entities], facet.id) // TODO add Facet.Focuses once Project level IP enabled
    ) {
      return "autocomplete";
    } else if (includes([Facet.Countries, Facet.Topics], facet.id)) {
      return "multiple-list";
    } else if (includes([Facet.TwitterCertified, Facet.Favorite], facet.id)) {
      return "boolean";
    } else if (includes([Facet.Followers], facet.id)) {
      return "min-max";
    } else if (includes([Facet.ReviewRate, Facet.AuthorBirthday], facet.id)) {
      return "slider";
    } else if (facet.id === Facet.Query) {
      return "search";
    } else if (facet.id === Facet.DocIds) {
      return "text";
    } else if (facet.id === Facet.AuthorType) {
      return "string-single-value";
    } else {
      return "";
    }
  }
}

export function getLabelFromReviewValue(item, facet: Facet) {
  if (facet !== Facet.Product || !item.topHits) {
    return item.label || item.key;
  } else {
    return get(item.topHits[0], "product.name", item.key);
  }
}

export function getFacetDescription(
  locale: Locale,
  facet: Facet,
  isGlobalInsightPage: boolean,
  isInsightPage: boolean
) {
  const facets = [
    Facet.Focuses,
    Facet.SpamType,
    Facet.Corpus,
    Facet.Countries,
    Facet.Genders,
    Facet.AuthorBirthday,
    Facet.AccountType,
    Facet.TwitterCertified,
    Facet.ProfileType,
    Facet.Followers,
    Facet.Devices,
    Facet.DevicesPublishingMethods,
    Facet.DeviceOperatingSystems,
    Facet.GeoLocations,
    Facet.GeoTags,
    Facet.DocIds,
  ];

  if (!includes(facets, facet)) {
    return null;
  }

  switch (facet) {
    case Facet.AuthorBirthday:
      return tr(locale, "FACETS::AGE::DESCRIPTION");
    case Facet.Followers:
      return tr(locale, "FACETS::AUTHORFOLLOWERS::DESCRIPTION");
    case Facet.Devices:
    case Facet.DevicesPublishingMethods:
    case Facet.DeviceOperatingSystems:
      return tr(locale, "FACETS::" + Facet.Device + "::DESCRIPTION");
    case Facet.Focuses:
      if (isGlobalInsightPage) {
        return tr(locale, "FACETS::" + facet + "::DESCRIPTION_GLOBAL");
      }
      if (isInsightPage) {
        return tr(locale, "FACETS::" + facet + "::DESCRIPTION");
      }
      return "";
    case Facet.DocIds:
      return tr(locale, "FACETS::DOCIDS_DESCRIPTION");
    default:
      return tr(locale, "FACETS::" + facet + "::DESCRIPTION");
  }
}

export const categories: Record<keyof typeof Categories, Facet[]> = {
  basic: [
    Facet.Query,
    Facet.Focuses,
    Facet.Platforms,
    Facet.Tones,
    Facet.Languages,
    Facet.Hashtags,
    Facet.SpamType,
    Facet.Media,
  ],
  team: [Facet.Favorite, Facet.Corpus, Facet.InfluencersTags],
  text: [
    Facet.ContentClassification,
    Facet.Topics,
    Facet.Entities,
    Facet.Emotions,
    Facet.Mentions,
  ],
  image: [
    Facet.ImageRecognitionLogos,
    Facet.ImageRecognitionPersonIdentifications,
    Facet.ImageRecognitionPersons,
    Facet.ImageRecognitionEmotions,
    Facet.ImageRecognitionGenders,
    Facet.ImageRecognitionAge,
  ],
  posts: [Facet.Countries],
  author: [
    Facet.Genders,
    Facet.AuthorBirthday,
    Facet.AccountType,
    Facet.TwitterCertified,
    Facet.AuthorType,
    Facet.ProfileType,
    Facet.Followers,
  ],
  misc: [
    Facet.ReviewRate,
    Facet.Devices,
    Facet.DevicesPublishingMethods,
    Facet.DeviceOperatingSystems,
    Facet.DocIds,
  ],
  reviews: [
    Facet.ReviewRate,
    Facet.Franchise,
    Facet.ProductRange,
    Facet.Product,
  ],
};

export function isCustomField(facet: FullFacet) {
  return facet && facet.custom;
}

export function isClosedCustomField(facet: FullFacet) {
  return isCustomField(facet) && facet?.type === "closed";
}

function getCleanField(field: string): string {
  if (field === "withRT") return Facet.Retweets;
  if (field === "favorite") return Facet.Favorite;
  if (field === "trash") return Facet.Trash;
  if (field === "verified") return Facet.TwitterCertified;
  if (field === "userUpdated") return Facet.UserUpdated;
  if (field === "inRelationship") return Facet.MaritalStatus;
  if (field === "hasStory") return Facet.Stories;
  if (field === "age") return Facet.AuthorBirthday;
  if (field === "mentions") return Facet.Mentions;
  if (field === "devicePublishingMethods")
    return Facet.DevicesPublishingMethods;
  if (field === "operatingSystems") return Facet.DeviceOperatingSystems;
  if (field === "inRelationship" || field === "hasChildren")
    return Facet.MaritalStatus;
  return field;
}

export function getFacetFromField(
  facets: Record<string, FullFacet>,
  field: string
): FullFacet {
  const cleanField = getCleanField(field);
  return facets[cleanField] || null;
}

export function getLegacyFacetFromField(field: string): string {
  if (field === Facet.AuthorBirthday) return "age";
  if (field === Facet.Followers) return "authorFollowers";
  if (field === Facet.ImageRecognitionPersonIdentifications)
    return "personIdentifications";
  if (field === Facet.ImageRecognitionLogos) return "logos";
  if (field === Facet.ImageRecognitionAge) return "imageAges";
  if (field === Facet.ImageRecognitionPersons) return "persons";
  if (field === Facet.ImageRecognitionGenders) return "imageGenders";
  if (field === Facet.Mentions) return "mentions";
  if (field === Facet.DevicesPublishingMethods) return "publishingMethods";
  if (field === Facet.DeviceOperatingSystems) return "operatingSystems";
  if (field === "inRelationship" || field === "hasChildren")
    return Facet.MaritalStatus;
  return field;
}

export function getLegacySandboxValue(value: string): string {
  if (value === "inRelationship") return "relationship";
  if (value === "hasChildren") return "children";
  return value;
}

export function isLegacyNumberIdsFacet(field: string): boolean {
  return (
    field === Facet.ImageRecognitionLogos ||
    field === Facet.ImageRecognitionPersonIdentifications ||
    field === Facet.Corpus
  );
}

export function isSanboxedFacet(facet: string): boolean {
  return facet === Facet.MaritalStatus;
}
